import React, { useEffect, useState } from 'react';
import { ArchiveIcon, ArchiveRestoreIcon, CheckIcon, CloudOffIcon, CopyIcon, DownloadIcon, EyeIcon, KeyRoundIcon, LockIcon, MoreHorizontalIcon, PinIcon, PinOffIcon, RefreshCwIcon, Share2Icon, ShieldIcon, Trash2Icon, TriangleAlertIcon, UnlockIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { BlockEditor } from './BlockEditor';
import { NoteMetaStrip } from './NoteMetaStrip';
import { ShareDialog } from './ShareDialog';
import { ExportDialog } from './ExportDialog';
import { CommentsPanel } from './CommentsPanel';
import { VersionHistory } from './VersionHistory';
import { PresenceBar } from './PresenceBar';
import { PinInput } from './PinInput';
import { PinSetupDialog } from './PinSetupDialog';
import { PinResetDialog } from './PinResetDialog';
import { attemptsLeft, cooldownRemaining } from '../../utils/notesLock';
import { useNotes } from '../../contexts/NotesContext';
import type { Note } from '../../types/notes';
interface NoteEditorPaneProps {
  note: Note;
  onDeleted: () => void;
}
function SaveIndicator() {
  const {
    saveState
  } = useNotes();
  if (saveState === 'idle') return null;
  const map: Record<Exclude<typeof saveState, 'idle'>, {
    label: string;
    icon: React.ReactNode;
    tone: 'neutral' | 'ok' | 'warn';
  }> = {
    saving: {
      label: 'Saving…',
      icon: <RefreshCwIcon className="h-3.5 w-3.5" data-id="element-3579" data-mp="2q5lgu9zs9l7" />,
      tone: 'neutral' as const
    },
    saved: {
      label: 'Saved',
      icon: <CheckIcon className="h-3.5 w-3.5" data-id="element-3580" data-mp="3ut6j3eoup4k1" />,
      tone: 'ok' as const
    },
    offline: {
      label: 'Offline — changes will sync when you reconnect',
      icon: <CloudOffIcon className="h-3.5 w-3.5" data-id="element-3581" data-mp="3pwg4ywwlwj19" />,
      tone: 'warn' as const
    },
    conflict: {
      label: 'Sync conflict',
      icon: <TriangleAlertIcon className="h-3.5 w-3.5" data-id="element-3582" data-mp="2eewr8alegxog" />,
      tone: 'warn'
    }
  };
  const current = map[saveState as Exclude<typeof saveState, 'idle'>];
  return <Badge tone={current.tone} icon={current.icon} data-id="element-3583" data-mp="29i6fu0kjiq9d">
      {current.label}
    </Badge>;
}
export function NoteEditorPane({
  note,
  onDeleted
}: NoteEditorPaneProps) {
  const {
    roleFor,
    setBlocks,
    setTitle,
    togglePin,
    archiveNote,
    restoreNote,
    trashNote,
    destroyNote,
    duplicateNote,
    setLocked,
    resolveConflict,
    presenceFor,
    lockCredential,
    unlockedNotes,
    attemptUnlock
  } = useNotes();
  const presence = presenceFor(note.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupMode, setSetupMode] = useState<'create' | 'change' | 'remove'>('create');
  const [resetOpen, setResetOpen] = useState(false);
  /** Lock this note as soon as the PIN has been created. */
  const [pendingLock, setPendingLock] = useState(false);
  const role = roleFor(note);
  const readOnly = role !== 'owner' && role !== 'editor';
  useEffect(() => {
    setPin('');
    setPinError(false);
    setMenuOpen(false);
  }, [note.id]);

  /** Drive the lockout countdown. */
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lockCredential?.cooldownUntil) return;
    const interval = window.setInterval(() => setTick(value => value + 1), 1000);
    return () => window.clearInterval(interval);
  }, [lockCredential?.cooldownUntil]);

  /** Once the PIN exists, apply the lock the user asked for. */
  useEffect(() => {
    if (pendingLock && lockCredential) {
      setLocked(note.id, true);
      setPendingLock(false);
    }
  }, [pendingLock, lockCredential, note.id, setLocked]);
  if (role === null) {
    return <div className="flex h-full items-center justify-center p-8" data-id="element-3584" data-mp="1wajvm1z3923q">
        <div className="max-w-sm text-center" data-id="element-3585" data-mp="1watnxw7lvlcl">
          <ShieldIcon className="mx-auto h-5 w-5 text-ink-subtle" data-id="element-3586" data-mp="1s9i5can5g0dw" />
          <p className="mt-3 text-[15px] font-semibold text-ink" data-id="element-3587" data-mp="1s75e16vi4do6">You do not have access</p>
          <p className="mt-1.5 text-[13px] leading-6 text-ink-muted" data-id="element-3588" data-mp="3lpl65e1u9a65">
            This note belongs to {note.ownerName} and has not been shared with you. Campuzon refuses
            the request on the server, so nothing about its contents is returned.
          </p>
        </div>
      </div>;
  }
  if (note.locked && !unlockedNotes.includes(note.id)) {
    const cooldown = cooldownRemaining(lockCredential);
    const left = attemptsLeft(lockCredential);
    return <>
        <div className="flex h-full items-center justify-center p-8" data-id="element-3589" data-mp="37sfzpcy7q9ad">
          <div className="w-full max-w-sm text-center" data-id="element-3590" data-mp="3cpy20khq5q9e">
            <LockIcon className="mx-auto h-5 w-5 text-ink-subtle" data-id="element-3591" data-mp="3ctdgbl3kqv9r" />
            <p className="mt-3 text-[15px] font-semibold text-ink" data-id="element-3592" data-mp="3gu5sam3tze9q">{note.title}</p>
            <p className="mt-1.5 text-[13px] leading-6 text-ink-muted" data-id="element-3593" data-mp="s00aafkwez5i">
              {lockCredential ? 'This note is locked. Enter your Notes PIN to read it.' : 'This note is marked as locked, but no Notes PIN has been set on this account yet. Set one to protect it.'}
            </p>
            {!lockCredential && <div className="mt-4 space-y-2" data-id="element-3594" data-mp="27s46sdvfzerl">
                <Button variant="primary" className="w-full" onClick={() => {
              setSetupMode('create');
              setSetupOpen(true);
            }} data-id="element-3595" data-mp="27qt30e1zy70h">
                  Set a Notes PIN
                </Button>
                <Button variant="secondary" className="w-full" onClick={() => setLocked(note.id, false)} data-id="element-3596" data-mp="1sta8ev2n0zl4">
                  Remove the lock instead
                </Button>
              </div>}
            {lockCredential && <form onSubmit={event => {
            event.preventDefault();
            if (attemptUnlock(pin)) {
              setPin('');
              setPinError(false);
            } else {
              setPinError(true);
              setPin('');
            }
          }} className="mt-4" data-id="element-3597" data-mp="3j6hof0zlqxz6">
              <PinInput value={pin} onChange={value => {
              setPin(value);
              setPinError(false);
            }} label="Notes PIN" autoFocus invalid={pinError} disabled={cooldown > 0} data-id="element-3598" data-mp="3odefw1mo6lxb" />
              {pinError && cooldown === 0 && <p className="mt-1.5 text-[12px] text-danger-700" data-id="element-3599" data-mp="ay017ekzt64l">
                  That PIN is not correct. {left} attempt{left === 1 ? '' : 's'} left before a short
                  wait.
                </p>}
              {cooldown > 0 && <p className="cz-num mt-1.5 text-[12px] text-warn-700" data-id="element-3600" data-mp="2vpscatdfzhag">
                  Too many attempts. Try again in {cooldown}s.
                </p>}
              <Button type="submit" variant="primary" className="mt-3 w-full" disabled={pin.length < 4 || cooldown > 0} data-id="element-3601" data-mp="2t1gbk5yazpxr">
                Unlock
              </Button>
            </form>}
            {lockCredential && <button type="button" onClick={() => setResetOpen(true)} className="mt-3 text-[12px] font-medium text-brand-800 hover:underline" data-id="element-3602" data-mp="2x2icqsbs6c2d">
                I have forgotten my PIN
              </button>}
            <p className="mt-4 text-[12px] leading-5 text-ink-muted" data-id="element-3603" data-mp="2j6ek33gk2bs0">
              Locking protects this note from someone holding your device. Ownership and authorisation
              are enforced separately by the Notes service — this is never the only protection.
            </p>
          </div>
        </div>
        <PinResetDialog open={resetOpen} onClose={() => setResetOpen(false)} data-id="element-3604" data-mp="1lp66ybwgnjdk" />
        <PinSetupDialog open={setupOpen} mode={setupMode} onClose={() => setSetupOpen(false)} onForgot={() => {
        setSetupOpen(false);
        setResetOpen(true);
      }} data-id="element-3605" data-mp="114xkwoxw5ete" />
      </>;
  }
  return <div className="flex h-full flex-col" data-id="element-3606" data-mp="1f3x2xdqwevgl">
      <header className="sticky top-0 z-10 border-b border-line bg-surface" data-id="element-3607" data-mp="1f4oxqmco8p5t">
        <div className="flex flex-wrap items-start gap-3 px-5 py-3.5 sm:px-8" data-id="element-3608" data-mp="1k1p3sx9itrwx">
          <div className="min-w-0 flex-1" data-id="element-3609" data-mp="1o3ace6f0vk1b">
            <input value={note.title} readOnly={readOnly} onChange={event => setTitle(note.id, event.target.value)} placeholder="Untitled note" aria-label="Note title" className="w-full bg-transparent text-xl font-semibold leading-8 text-ink placeholder:text-ink-subtle focus:outline-none" data-id="element-3610" data-mp="1o292i59qt3l6" />
            <div className="mt-1 flex flex-wrap items-center gap-2" data-id="element-3611" data-mp="27skolafk7muo">
              <span className="cz-num text-[12px] text-ink-subtle" data-id="element-3612" data-mp="3saddud1v7arm">
                Edited {note.updatedAt} · {note.devices.length} device
                {note.devices.length === 1 ? '' : 's'}
              </span>
              <SaveIndicator data-id="element-3613" data-mp="3icpaiyq70uxu" />
              {readOnly && <Badge tone="warn" icon={<EyeIcon className="h-3.5 w-3.5" data-id="element-3615" data-mp="3ddw75z4mekbe" />} data-id="element-3614" data-mp="3dd2w4lymp8ng">
                  View only — shared by {note.ownerName}
                </Badge>}
              {role === 'editor' && <Badge tone="brand" data-id="element-3616" data-mp="9vuf24bq6cts">Editor · shared by {note.ownerName}</Badge>}
              {note.isPrivate && role === 'owner' && <Badge tone="neutral" data-id="element-3617" data-mp="53t8mz18ngbo">Private</Badge>}
              {note.diaryDate && <Badge tone="brand" data-id="element-3618" data-mp="72pu3y1u6mk">Diary · {note.diaryDate}</Badge>}
              {note.mood && <Badge tone="neutral" data-id="element-3619" data-mp="xm8am4tiqtt4">Mood: {note.mood}</Badge>}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5" data-id="element-3620" data-mp="sv8dozkegbqm">
            {role === 'owner' && <>
                <Button size="sm" variant="secondary" icon={<Share2Icon className="h-3.5 w-3.5" data-id="element-3622" data-mp="19tflsa2wf2wd" />} onClick={() => setShareOpen(true)} data-id="element-3621" data-mp="1dthdpriu0hyg">
                  Share
                </Button>
                <Button size="sm" variant="ghost" aria-label={note.pinned ? 'Unpin note' : 'Pin note'} onClick={() => togglePin(note.id)} data-id="element-3623" data-mp="1itltffbx18nm">
                  {note.pinned ? <PinOffIcon className="h-4 w-4" data-id="element-3624" data-mp="1spfejn3paah0" /> : <PinIcon className="h-4 w-4" data-id="element-3625" data-mp="26hllqjovl74b" />}
                </Button>
              </>}
            <div className="relative" data-id="element-3626" data-mp="21lwr8urpqgev">
              <Button size="sm" variant="ghost" aria-label="More actions" aria-expanded={menuOpen} onClick={() => setMenuOpen(value => !value)} data-id="element-3627" data-mp="2ggkv64hgt6sd">
                <MoreHorizontalIcon className="h-4 w-4" data-id="element-3628" data-mp="2v4b64bwna8h0" />
              </Button>
              {menuOpen && <div className="absolute right-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-card border border-line bg-surface shadow-pop" data-id="element-3629" data-mp="2z3agwd524zgd">
                  <ul className="py-1 text-[13px]" data-id="element-3630" data-mp="2q46uaz6om1x4">
                    <li data-id="element-3631" data-mp="2q6t3f3980phg">
                      <button type="button" onClick={() => {
                    setExportOpen(true);
                    setMenuOpen(false);
                  }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink hover:bg-canvas" data-id="element-3632" data-mp="2bftv7tcas2ye">
                        <DownloadIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3633" data-mp="27egq5lh143sj" /> Export
                      </button>
                    </li>
                    {role === 'owner' && <>
                        <li data-id="element-3634" data-mp="1xi5ft1101o0q">
                          <button type="button" onClick={() => {
                      duplicateNote(note.id);
                      setMenuOpen(false);
                    }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink hover:bg-canvas" data-id="element-3635" data-mp="1ebxtv1p2hy2j">
                            <CopyIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3636" data-mp="1xato2d0xecgd" /> Duplicate
                          </button>
                        </li>
                        <li data-id="element-3637" data-mp="1sbyseahv0teu">
                          <button type="button" onClick={() => {
                      setMenuOpen(false);
                      if (note.locked) {
                        setLocked(note.id, false);
                      } else if (lockCredential) {
                        setLocked(note.id, true);
                      } else {
                        setSetupMode('create');
                        setPendingLock(true);
                        setSetupOpen(true);
                      }
                    }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink hover:bg-canvas" data-id="element-3638" data-mp="1nc42oa9tm9vd">
                            {note.locked ? <UnlockIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3639" data-mp="328f58ofcncle" /> : <LockIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3640" data-mp="368z1zqw0ge70" />}
                            {note.locked ? 'Remove lock from this note' : lockCredential ? 'Lock this note' : 'Lock with PIN…'}
                          </button>
                        </li>
                        {lockCredential && <li data-id="element-3641" data-mp="3cazn4zvx8jka">
                            <button type="button" onClick={() => {
                      setSetupMode('change');
                      setSetupOpen(true);
                      setMenuOpen(false);
                    }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink hover:bg-canvas" data-id="element-3642" data-mp="3ce5b2bstdfef">
                              <KeyRoundIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3643" data-mp="2onj64te3wb8n" /> Change Notes PIN
                            </button>
                          </li>}
                        <li data-id="element-3644" data-mp="2xm3eb0m1j2im">
                          <button type="button" onClick={() => {
                      if (note.lifecycle === 'archived') restoreNote(note.id);else archiveNote(note.id);
                      setMenuOpen(false);
                    }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink hover:bg-canvas" data-id="element-3645" data-mp="2xlv3de9ha7a2">
                            {note.lifecycle === 'archived' ? <ArchiveRestoreIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3646" data-mp="1qs5mxbmwd557" /> : <ArchiveIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3647" data-mp="2ejuhkyn6y5pb" />}
                            {note.lifecycle === 'archived' ? 'Restore from archive' : 'Archive'}
                          </button>
                        </li>
                        <li className="border-t border-line" data-id="element-3648" data-mp="29indb9k9bone">
                          {note.lifecycle === 'trashed' ? <>
                              <button type="button" onClick={() => {
                        restoreNote(note.id);
                        setMenuOpen(false);
                      }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-ink hover:bg-canvas" data-id="element-3649" data-mp="t7jd9sgtkxr8">
                                <ArchiveRestoreIcon className="h-3.5 w-3.5 text-ink-subtle" data-id="element-3650" data-mp="2m2kazgplxq2z" /> Restore
                              </button>
                              <button type="button" onClick={() => {
                        destroyNote(note.id);
                        onDeleted();
                      }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-danger-700 hover:bg-danger-50" data-id="element-3651" data-mp="2h4iriuo9roqz">
                                <Trash2Icon className="h-3.5 w-3.5" data-id="element-3652" data-mp="68jy1doebx9r" /> Delete permanently
                              </button>
                            </> : <button type="button" onClick={() => {
                      trashNote(note.id);
                      setMenuOpen(false);
                    }} className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-danger-700 hover:bg-danger-50" data-id="element-3653" data-mp="8t9kb36koe4v">
                              <Trash2Icon className="h-3.5 w-3.5" data-id="element-3654" data-mp="3r0pavb1i9in3" /> Move to trash
                            </button>}
                        </li>
                      </>}
                  </ul>
                </div>}
            </div>
          </div>
        </div>

        {note.lifecycle === 'trashed' && <p className="border-t border-warn-100 bg-warn-50 px-5 py-2 text-[12px] text-ink sm:px-8" data-id="element-3655" data-mp="1bbxelbpq4swf">
            In trash since {note.trashedOn}. It will be permanently removed 30 days after deletion.
          </p>}

        {note.syncState === 'conflict' && <div className="flex flex-wrap items-center gap-3 border-t border-warn-100 bg-warn-50 px-5 py-2.5 sm:px-8" data-id="element-3656" data-mp="1wf6jv90ri8x8">
            <p className="min-w-0 flex-1 text-[12px] leading-5 text-ink" data-id="element-3657" data-mp="2b42pz0j9dbv7">
              This note was edited on another device while you were offline. Nothing has been
              overwritten — choose which version to keep.
            </p>
            <Button size="sm" variant="secondary" onClick={() => resolveConflict(note.id, 'mine')} data-id="element-3658" data-mp="2g24isd6hkpjv">
              Keep this device
            </Button>
            <Button size="sm" variant="secondary" onClick={() => resolveConflict(note.id, 'theirs')} data-id="element-3659" data-mp="2u1400zro6260">
              Keep the other version
            </Button>
          </div>}
      </header>

      <PresenceBar note={note} presence={presence} data-id="element-3660" data-mp="zo4kf6nah7yq" />
      <NoteMetaStrip note={note} readOnly={readOnly} data-id="element-3661" data-mp="up1gxqmyj9sv" />

      <div className="min-h-0 flex-1 overflow-y-auto" data-id="element-3662" data-mp="uqmc9ih3iwrs">
        {readOnly && <p className="border-b border-line bg-canvas px-5 py-2.5 text-[12px] leading-5 text-ink-muted sm:px-8" data-id="element-3663" data-mp="36cdeezv3rjyi">
            {note.ownerName} shared this as view only, so the formatting controls are not available to
            you. Ask them to change your role to editor if you need to make changes.
          </p>}
        <BlockEditor blocks={note.blocks} onChange={blocks => setBlocks(note.id, blocks)} readOnly={readOnly} presence={presence} data-id="element-3664" data-mp="31j26swiguz5j" />

        <CommentsPanel note={note} role={role} data-id="element-3665" data-mp="2slrrn02j2pvc" />
        <VersionHistory note={note} role={role} data-id="element-3666" data-mp="2dnpm4kbxrubg" />
      </div>

      <ShareDialog note={note} open={shareOpen} onClose={() => setShareOpen(false)} data-id="element-3667" data-mp="2dpajsitjrtdj" />
      <ExportDialog note={note} open={exportOpen} onClose={() => setExportOpen(false)} data-id="element-3668" data-mp="2imj2qlmis1fu" />
      <PinSetupDialog open={setupOpen} mode={setupMode} onClose={() => {
      setSetupOpen(false);
      setPendingLock(false);
    }} onForgot={() => {
      setSetupOpen(false);
      setResetOpen(true);
    }} data-id="element-3669" data-mp="bwckc459m2jm" />
      <PinResetDialog open={resetOpen} onClose={() => setResetOpen(false)} data-id="element-3670" data-mp="1p629pnn530f7" />
    </div>;
}